import React, { useMemo } from 'react';
import Icon from 'src/components/Icon';
import styles from '../styles.module.scss';
import {
    ExtractionSchemaRecord,
    SchemaPath,
    describeFieldSummary,
    deserializePath,
    getPathId,
    getSchemaProperties,
    getSchemaRequired,
    getValueDefinition,
    isRecord,
    serializePath,
} from './schemaModel';
import { EditorActions, EditorUiState } from './editorTypes';
import FieldRow from './FieldRow';

interface FieldListProps {
    objectSchema: ExtractionSchemaRecord;
    serializedObjectPath: string;
    depth: number;
    ui: EditorUiState;
    actions: EditorActions;
}

export default function FieldList({ objectSchema, serializedObjectPath, depth, ui, actions }: FieldListProps) {
    const objectPath = useMemo(() => deserializePath(serializedObjectPath), [serializedObjectPath]);
    const pathId = getPathId(objectPath) || 'root';
    const entries = Object.entries(getSchemaProperties(objectSchema)).filter(
        (entry): entry is [string, ExtractionSchemaRecord] => isRecord(entry[1]),
    );
    const requiredKeys = new Set(getSchemaRequired(objectSchema));
    const isFiltering = !!ui.filter;

    const rows = entries.map(([propertyKey, propertySchema], index) => {
        const rowPathId = getPathId(objectPath, propertyKey);
        const serializedPropertyPath = serializePath(objectPath, propertyKey);
        if (ui.filter && !ui.filter.visiblePaths.has(serializedPropertyPath)) {
            return null;
        }
        const propertyPath: SchemaPath = [...objectPath, { kind: 'property', key: propertyKey }];
        const definition = getValueDefinition(propertySchema, propertyPath);
        const isGroup = definition.type === 'object';
        const fieldId = actions.getFieldId(serializedPropertyPath);
        const isExpanded = isGroup && (isFiltering || ui.expandedGroupIds.has(fieldId));
        const title = typeof propertySchema.title === 'string' ? propertySchema.title : '';
        const description = typeof propertySchema.description === 'string' ? propertySchema.description : '';

        return (
            <FieldRow
                key={fieldId}
                pathId={rowPathId}
                fieldId={fieldId}
                propertyKey={propertyKey}
                serializedObjectPath={serializedObjectPath}
                serializedPropertyPath={serializedPropertyPath}
                displayName={title.trim() || propertyKey}
                title={title}
                description={description}
                summary={describeFieldSummary(propertySchema)}
                valueType={definition.type}
                arrayDepth={definition.arrayDepth}
                required={requiredKeys.has(propertyKey)}
                isGroup={isGroup}
                isExpanded={isExpanded}
                isActive={ui.activeFieldId === fieldId}
                isFiltering={isFiltering}
                canMoveUp={index > 0}
                canMoveDown={index < entries.length - 1}
                editable={ui.editable}
                actions={actions}
            >
                {isGroup && isExpanded ? (
                    <FieldList
                        objectSchema={definition.valueSchema}
                        serializedObjectPath={serializePath(definition.valuePath)}
                        depth={depth + 1}
                        ui={ui}
                        actions={actions}
                    />
                ) : null}
            </FieldRow>
        );
    });
    const visibleRows = rows.filter(Boolean);

    return (
        <div className={styles.extractionSchemaObject} data-test-id={`extraction-schema-object-${pathId}`}>
            {entries.length === 0 ? (
                <div className={styles.extractionSchemaEmpty} data-test-id={`extraction-schema-empty-${pathId}`}>
                    {depth === 0 ? (
                        <>
                            <strong>No fields yet</strong>
                            <span>
                                Add each piece of information Capture AI should read from this document — for example
                                Patient name or Date of birth.
                            </span>
                        </>
                    ) : (
                        <span>This group has no fields yet.</span>
                    )}
                </div>
            ) : visibleRows.length ? (
                <ul className={styles.extractionSchemaProperties}>{rows}</ul>
            ) : null}
            {ui.editable && !isFiltering ? (
                <button
                    type="button"
                    className={styles.extractionSchemaAddLink}
                    onClick={() => actions.addField(serializedObjectPath)}
                    data-test-id={`extraction-schema-add-${pathId}`}
                >
                    <Icon type="fa" name="plus" aria-hidden="true" />
                    {depth === 0 ? (entries.length === 0 ? 'Add your first field' : 'Add field') : 'Add field to group'}
                </button>
            ) : null}
        </div>
    );
}
